<!-- BaseTinyEditor.vue (common) -->
<template tag="공통 에디터 컴포넌트">
  <div>
    <Editor
      ref="editorRef"
      v-if="isEditorReady"
      :key="`admin-${editorKey}`"
      v-model="model"
      :init="adminInitOptions"
      :readonly="readonly"
      :tinymceScriptSrc="tinymceScript"
    />
    <!-- 로딩 상태 -->
    <div v-else class="editor-loading">loading...</div>

    <!-- Admin 모드에서만 글자수 표시 -->
    <div
      v-if="data === 'admin' && enableLimit"
      class="text-right mt-2"
      style="font-size: 13px; color: #666"
    >
      {{ $text("editor.placeholder") }} :
      <span class="mx-1" :class="textLength > maxLength ? 'text-error' : ''">
        {{ textLength }}
      </span>
      <span>/</span>
      <span>{{ maxLength }}</span>
    </div>

    <!-- BasePopupView를 활용한 미리보기 -->
    <BasePopupView v-model="showPreview">
      <template #title>📄{{ $text("preview") }}</template>

      <div v-dompurify-html="sanitizedContent"></div>

      <template #footer="{ close }">
        <button class="btn btn_default btn_md" @click="close" type="button">
          {{ $text("button.close") }}
        </button>
      </template>
    </BasePopupView>
  </div>
</template>

<script setup lang="ts">
import { global, i18n } from "@/common/plugins/global.ts";

import {
  ref,
  defineProps,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  nextTick,
  onActivated,
  onDeactivated,
  watch,
} from "vue";
import Editor from "@tinymce/tinymce-vue";
import { type Editor as EditorType } from "tinymce";
import BasePopupView from "@/common/base-components/client/BasePopup.vue";

// 환경변수로 모드 구분
const data = import.meta.env.VITE_APP_TYPE;

// dynamically resolve base path
const basePath = import.meta.env.BASE_URL;
const tinymceScript = basePath + "tinymce/tinymce.min.js";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const { $text } = i18n();
const vm = global();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
/** admin 모드용  */
const model = defineModel<string>();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  id: { type: String, default: "" },
  readonly: { type: Boolean, default: false }, // admin 모드용
  disabled: { type: Boolean, default: false }, // 일반 모드용
  maxLength: { type: Number, default: 3000 },
  enableLimit: { type: Boolean, default: true },
  height: { type: Number, default: 700 },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const editorRef = ref();
const editorInstance = ref<EditorType | null>(null);
const textLength = ref(0);
const isEditorReady = ref(false);
const editorKey = ref(Date.now()); // 고유 키 생성
const storedLang = sessionStorage.getItem("lang");

// CSP 안전한 미리보기 모달 상태
const showPreview = ref(false);
const sanitizedContent = ref("");

// Admin 모드 TinyMCE 옵션
const adminInitOptions = {
  base_url: basePath + "tinymce", // self-hosted base
  height: props.height,
  menubar: "format insert tools table",
  statusbar: false,
  branding: false,
  promotion: false,
  plugins: "lists link code table media",
  toolbar:
    "undo redo | formatselect | bold italic | forecolor backcolor |" +
    "alignleft aligncenter alignright alignjustify | " +
    "bullist numlist outdent indent | customimage link table | removeformat | code media",
  elementpath: false,
  paste_data_images: true,
  paste_webkit_styles: "all",
  paste_merge_formats: true,
  valid_elements: "*[*]",
  content_css: basePath + "tinymce/tiny-editor-content.css",
  language: sessionStorage.getItem("lang"),
  language_url: `/tinymce/lang/${storedLang}.js`,
  //the Tiny Community Language Packages download page: https://www.tiny.cloud/get-tiny/language-packages/
  setup: function (editor: EditorType) {
    // 에러 처리 추가
    editor.on("error", (e: unknown) => {
      console.error("TinyMCE 런타임 에러:", e);
    });

    editor.on("keydown", function (e: KeyboardEvent) {
      if (textLength.value >= props.maxLength && e.keyCode !== 8 && e.keyCode !== 46) {
        e.preventDefault();
        vm.$toast($text("editor.maxLength"), "error");
      }
    });

    editor.on("input keyup setcontent", function () {
      const content = editor.getContent();
      const textOnly = content
        .replace(/<img[^>]*>/gi, "") // 이미지 태그만 별도 제거
        .replace(/<[^>]+>/g, "") // 안전한 HTML 태그 제거 (ReDoS 방지)
        .replace(/&nbsp;/g, " ") // &nbsp; → 공백
        .replace(/&[a-zA-Z0-9#]+;/g, "") // HTML 엔티티 제거
        .replace(/\s+/g, " ") // 연속 공백 → 단일 공백
        .trim();
      textLength.value = textOnly.length;
    });

    editor.on("paste", function (e: ClipboardEvent) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      const pastedText = clipboardData.getData("text/plain");
      const allowedLength = Math.max(props.maxLength - textLength.value, 0);

      if (pastedText.length > allowedLength) {
        e.preventDefault();
        if (allowedLength > 0) {
          const trimmedText = pastedText.slice(0, allowedLength);
          editor.insertContent(trimmedText);
        }
        vm.$toast($text("editor.maxLength"), "error");
      }
    });
    editorInstance.value = editor;

    editor.ui.registry.addButton("customimage", {
      icon: "image",
      tooltip: $text("editor.imageTooltip"),
      onAction: () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp";

        input.onchange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          const files = target.files;
          if (files && files.length > 0) {
            const file = files[0];
            // 파일 타입 검증
            const allowedTypes = [
              "image/jpeg",
              "image/jpg",
              "image/png",
              "image/gif",
              "image/bmp",
              "image/webp",
            ];
            if (!allowedTypes.includes(file.type)) {
              vm.$toast($text("editor.noImage"), "error");
              return;
            }

            const reader = new FileReader();
            reader.onload = () => {
              editor.insertContent(`<img src="${reader.result}" alt="${file.name}" />`);
            };
            reader.readAsDataURL(file);
          }
        };

        input.click();
      },
    });
  },

  // media_url_resolver 옵션 추가
  media_url_resolver: () => {
    return new Promise((resolve) => {
      resolve({ html: "" });
    });
  },
};

/* ------------------------------------------------------------
                          watch
   ------------------------------------------------------------ */
/** 모달이 닫힐 때 content 초기화 */
watch(showPreview, (newVal) => {
  if (!newVal) {
    sanitizedContent.value = "";
  }
});

/* ------------------------------------------------------------
                                  Methods
   ------------------------------------------------------------ */
/** 에디터 정리 함수 */
const cleanupEditor = () => {
  isEditorReady.value = false;

  if (editorInstance.value) {
    try {
      if (!editorInstance.value.destroyed) {
        editorInstance.value.destroy();
      }
    } catch (error) {
      console.warn("에디터 정리 중 경고:", error);
    } finally {
      editorInstance.value = null;
    }
  }

  // TinyMCE 전역 인스턴스 정리
  try {
    if (window.$tinymce && window.$tinymce.editors) {
      const editors = window.$tinymce.editors.slice(); // 복사본 생성
      editors.forEach((editor: EditorType) => {
        try {
          if (editor && !editor.destroyed) {
            editor.destroy();
          }
        } catch (e) {
          console.warn("전역 에디터 정리 중 경고:", e);
        }
      });
    }
  } catch (error) {
    console.warn("TinyMCE 전역 정리 중 경고:", error);
  }
};

/** BasePopupView를 활용한 미리보기 실행 */
const executePreview = () => {
  const editor = editorInstance.value || editorRef.value?.editor;

  if (!editor) {
    vm.$toast("에디터가 로드되지 않았습니다.", "error");
    return;
  }

  try {
    const content = editor.getContent();

    if (!content || content.trim() === "") {
      vm.$toast("미리보기할 내용이 없습니다.", "warning");
      return;
    }

    // HTML sanitization 적용
    sanitizedContent.value = content; // 더 이상 정제 로직 제거

    // 모달 표시
    showPreview.value = true;
  } catch (error) {
    console.error("미리보기 실행 중 오류:", error);
    vm.$toast("미리보기를 실행할 수 없습니다.", "error");
  }
};

/* ------------------------------------------------------------
                          Lifecycle
   ------------------------------------------------------------ */
/** 페이지 렌더링 시 초기값 설정 */
onMounted(async () => {
  // DOM 안정화 후 에디터 활성화
  await nextTick();
  setTimeout(() => {
    isEditorReady.value = true;
  }, 100);
});

/** Admin 모드에서만 실행 */
onBeforeMount(() => {
  if (data === "admin") {
    (function () {
      const originalAddEventListener = EventTarget.prototype.addEventListener;
      EventTarget.prototype.addEventListener = function (type, listener, options) {
        if (["touchstart", "touchmove"].includes(type)) {
          if (typeof options === "boolean") {
            options = { capture: options, passive: true };
          } else if (typeof options === "object") {
            options = { ...options, passive: true };
          } else {
            options = { passive: true };
          }
        }
        return originalAddEventListener.call(this, type, listener, options);
      };
    })();
  }
});

/** 컴포넌트 언마운트 시 정리 */
onBeforeUnmount(() => {
  cleanupEditor();
});

/** keep-alive 사용 시 처리 */
onDeactivated(() => {
  cleanupEditor();
});

/** keep-alive 사용 시 처리 */
onActivated(async () => {
  await nextTick();
  setTimeout(() => {
    editorKey.value = Date.now(); // 새로운 키 생성
    isEditorReady.value = true;
  }, 100);
});

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
/** 외부에서 사용할 수 있도록 노출 */
defineExpose({
  executePreview,
  getEditor: () => editorRef.value?.editor,
});
</script>
